import { useMutation, UseMutationResult, useQuery, UseQueryResult, useQueryClient } from '@tanstack/react-query';
import { fetchSelector } from 'common/state/common';
import { API_URLS } from 'settings/api';
import { useRecoilValue } from 'recoil';
import { useRollbar } from '@rollbar/react';
import { processError } from 'utils/helpers';
import { Folder } from 'common/types/folders';
import { TRACKING_EVENTS } from 'common/types/tracking';
import { useTracking } from 'common/hooks/tracking';
import { PaginatedCharts } from 'common/types/charts';
import { DashboardDetails } from 'common/types/dashboard';
import { RawChartSchema } from 'common/types/chart';

const addFolderDashboard = (
  folderDashboards: DashboardDetails[] | undefined,
  dashboards: DashboardDetails[] | undefined,
  itemId: string,
): DashboardDetails[] | undefined => {
  if (!folderDashboards) return folderDashboards;

  const dashboard = dashboards?.find(entry => entry.uuid === itemId);
  if (!dashboard || folderDashboards.some(entry => entry.uuid === itemId)) return folderDashboards;

  return [...folderDashboards, dashboard];
};

const removeFolderDashboard = (folderDashboards: DashboardDetails[] | undefined, itemId: string): DashboardDetails[] | undefined => {
  if (!folderDashboards) return folderDashboards;

  return folderDashboards.filter(entry => entry.uuid !== itemId);
};

const removeFolderChart = (folderCharts: RawChartSchema[] | undefined, itemId: string): RawChartSchema[] | undefined => {
  if (!folderCharts) return folderCharts;

  return folderCharts.filter(entry => entry.uuid !== itemId);
};

const upsertFolderItem = (folders: Folder[] | undefined, folderId: string, itemId: string): Folder[] | undefined => {
  if (!folders) return folders;

  return folders.map(folder => {
    if (String(folder.id) !== String(folderId)) return folder;

    const itemSet = new Set(folder.items);
    itemSet.add(itemId);

    return {
      ...folder,
      items: Array.from(itemSet),
    };
  });
};

const removeFolderItem = (folders: Folder[] | undefined, folderId: string, itemId: string): Folder[] | undefined => {
  if (!folders) return folders;

  return folders.map(folder => {
    if (String(folder.id) !== String(folderId)) return folder;

    return {
      ...folder,
      items: folder.items.filter(id => id !== itemId),
    };
  });
};

const addDashboardFolderLink = (
  dashboards: DashboardDetails[] | undefined,
  folders: Folder[] | undefined,
  folderId: string,
  itemId: string,
): DashboardDetails[] | undefined => {
  if (!dashboards) return dashboards;

  const targetFolder = folders?.find(folder => String(folder.id) === String(folderId));
  if (!targetFolder) return dashboards;

  return dashboards.map(dashboard => {
    if (dashboard.uuid !== itemId) return dashboard;

    const alreadyLinked = dashboard.folders.some(folder => String(folder.id) === String(folderId));
    if (alreadyLinked) return dashboard;

    return {
      ...dashboard,
      folders: [
        ...dashboard.folders,
        {
          id: targetFolder.id,
          name: targetFolder.name,
          folder_type: targetFolder.folder_type,
          items: targetFolder.items,
        },
      ],
    };
  });
};

const removeDashboardFolderLink = (
  dashboards: DashboardDetails[] | undefined,
  folderId: string,
  itemId: string,
): DashboardDetails[] | undefined => {
  if (!dashboards) return dashboards;

  return dashboards.map(dashboard => {
    if (dashboard.uuid !== itemId) return dashboard;

    return {
      ...dashboard,
      folders: dashboard.folders.filter(folder => String(folder.id) !== String(folderId)),
    };
  });
};

export const useFolders = (): UseQueryResult<Folder[], unknown> => {
  const rollbar = useRollbar();
  const { fetchGet } = useRecoilValue(fetchSelector);

  return useQuery(
    ['folders'],
    async () => {
      try {
        return await fetchGet<Folder[]>(API_URLS.FOLDERS);
      } catch (e) {
        const boundRollbarError = rollbar.error.bind(rollbar);
        processError(e, boundRollbarError, 'Failed to fetch folders');
        return [];
      }
    },
    {
      staleTime: Infinity,
      placeholderData: [],
    },
  );
};

export const useCreateFolder = (): UseMutationResult<Folder, unknown, { name: string; folder_type: string }> => {
  const { fetchPost } = useRecoilValue(fetchSelector);
  const { trackEvent } = useTracking();

  return useMutation({
    mutationFn: async ({ name, folder_type = 'chart' }) => {
      const result = await fetchPost<Folder>(API_URLS.FOLDERS, {
        folder: {
          name,
          folder_type,
        },
      });

      trackEvent(TRACKING_EVENTS.CHART_FOLDER_CREATED, {
        folderName: name,
        folderType: folder_type,
      });

      return result;
    },
  });
};

export const useUpdateFolderName = (): UseMutationResult<Folder, unknown, { id: string; name: string }> => {
  const { fetchPut } = useRecoilValue(fetchSelector);
  const { trackEvent } = useTracking();

  return useMutation({
    mutationFn: async ({ id, name }) => {
      const result = await fetchPut<Folder>(`${API_URLS.FOLDERS}/${id}`, {
        folder: {
          name,
        },
      });

      trackEvent(TRACKING_EVENTS.CHART_FOLDER_RENAMED, {
        folderName: name,
      });

      return result;
    },
  });
};

export const useDeleteFolder = (): UseMutationResult<Folder, unknown, string> => {
  const { fetchDelete } = useRecoilValue(fetchSelector);
  const { trackEvent } = useTracking();

  return useMutation({
    mutationFn: async (folderId: string) => {
      const result = await fetchDelete<Folder>(`${API_URLS.FOLDERS}/${folderId}`);

      trackEvent(TRACKING_EVENTS.CHART_FOLDER_DELETED, {
        folderId,
      });

      return result;
    },
  });
};

export const useAddItemToFolder = (): UseMutationResult<Folder, unknown, { folderId: string; itemId: string }> => {
  const { fetchPost } = useRecoilValue(fetchSelector);
  const { trackEvent } = useTracking();
  const queryClient = useQueryClient();

  return useMutation({
    onMutate: async ({ folderId, itemId }) => {
      await Promise.all([
        queryClient.cancelQueries(['folders']),
        queryClient.cancelQueries(['dashboards']),
        queryClient.cancelQueries(['folder-dashboards', folderId]),
        queryClient.cancelQueries(['folder-charts', folderId]),
      ]);

      const previousFolders = queryClient.getQueryData<Folder[]>(['folders']);
      const previousDashboards = queryClient.getQueryData<DashboardDetails[]>(['dashboards']);
      const previousFolderDashboards = queryClient.getQueryData<DashboardDetails[]>(['folder-dashboards', folderId]);
      const previousFolderCharts = queryClient.getQueryData<RawChartSchema[]>(['folder-charts', folderId]);
      const nextFolders = upsertFolderItem(previousFolders, folderId, itemId);
      const nextDashboards = addDashboardFolderLink(previousDashboards, nextFolders, folderId, itemId);

      queryClient.setQueryData(['folders'], nextFolders);
      queryClient.setQueryData(['dashboards'], nextDashboards);
      queryClient.setQueryData(['folder-dashboards', folderId], addFolderDashboard(previousFolderDashboards, nextDashboards, itemId));
      queryClient.setQueryData(['folder-charts', folderId], previousFolderCharts);

      return { previousFolders, previousDashboards, previousFolderDashboards, previousFolderCharts };
    },
    mutationFn: async ({ folderId, itemId }) => {
      const result = await fetchPost<Folder>(`${API_URLS.FOLDERS}/${folderId}/add_item?item_uuid=${itemId}`);

      trackEvent(TRACKING_EVENTS.ITEM_ADDED_TO_FOLDER, {
        folderId,
        itemId,
      });

      return result;
    },
    onError: (_error, _variables, context) => {
      if (!context) return;

      queryClient.setQueryData(['folders'], context.previousFolders);
      queryClient.setQueryData(['dashboards'], context.previousDashboards);
      queryClient.setQueryData(['folder-dashboards', _variables.folderId], context.previousFolderDashboards);
      queryClient.setQueryData(['folder-charts', _variables.folderId], context.previousFolderCharts);
    },
    onSettled: async (_data, _error, variables) => {
      await Promise.all([
        queryClient.invalidateQueries(['folders']),
        queryClient.invalidateQueries(['dashboards']),
        queryClient.invalidateQueries(['folder-dashboards', variables.folderId]),
        queryClient.invalidateQueries(['folder-charts', variables.folderId]),
      ]);
    },
  });
};

export const useRemoveItemFromFolder = (): UseMutationResult<Folder, unknown, { folderId: string; itemId: string }> => {
  const { fetchDelete } = useRecoilValue(fetchSelector);
  const { trackEvent } = useTracking();
  const queryClient = useQueryClient();

  return useMutation({
    onMutate: async ({ folderId, itemId }) => {
      await Promise.all([
        queryClient.cancelQueries(['folders']),
        queryClient.cancelQueries(['dashboards']),
        queryClient.cancelQueries(['folder-dashboards', folderId]),
        queryClient.cancelQueries(['folder-charts', folderId]),
      ]);

      const previousFolders = queryClient.getQueryData<Folder[]>(['folders']);
      const previousDashboards = queryClient.getQueryData<DashboardDetails[]>(['dashboards']);
      const previousFolderDashboards = queryClient.getQueryData<DashboardDetails[]>(['folder-dashboards', folderId]);
      const previousFolderCharts = queryClient.getQueryData<RawChartSchema[]>(['folder-charts', folderId]);

      queryClient.setQueryData(['folders'], removeFolderItem(previousFolders, folderId, itemId));
      queryClient.setQueryData(['dashboards'], removeDashboardFolderLink(previousDashboards, folderId, itemId));
      queryClient.setQueryData(['folder-dashboards', folderId], removeFolderDashboard(previousFolderDashboards, itemId));
      queryClient.setQueryData(['folder-charts', folderId], removeFolderChart(previousFolderCharts, itemId));

      return { previousFolders, previousDashboards, previousFolderDashboards, previousFolderCharts };
    },
    mutationFn: async ({ folderId, itemId }) => {
      const result = await fetchDelete<Folder>(`${API_URLS.FOLDERS}/${folderId}/remove_item?item_uuid=${itemId}`);

      trackEvent(TRACKING_EVENTS.ITEM_REMOVED_FROM_FOLDER, {
        folderId,
        itemId,
      });

      return result;
    },
    onError: (_error, _variables, context) => {
      if (!context) return;

      queryClient.setQueryData(['folders'], context.previousFolders);
      queryClient.setQueryData(['dashboards'], context.previousDashboards);
      queryClient.setQueryData(['folder-dashboards', _variables.folderId], context.previousFolderDashboards);
      queryClient.setQueryData(['folder-charts', _variables.folderId], context.previousFolderCharts);
    },
    onSettled: async (_data, _error, variables) => {
      await Promise.all([
        queryClient.invalidateQueries(['folders']),
        queryClient.invalidateQueries(['dashboards']),
        queryClient.invalidateQueries(['folder-dashboards', variables.folderId]),
        queryClient.invalidateQueries(['folder-charts', variables.folderId]),
      ]);
    },
  });
};

export const useGetFolderCharts = (folderId: string | null): UseQueryResult<RawChartSchema[], unknown> => {
  const rollbar = useRollbar();
  const { fetchGet } = useRecoilValue(fetchSelector);

  return useQuery(
    ['folder-charts', folderId],
    async () => {
      if (!folderId) return [];

      try {
        const data = await fetchGet<PaginatedCharts>(`${API_URLS.FOLDERS}/${folderId}/charts`);
        return data.records;
      } catch (e) {
        const boundRollbarError = rollbar.error.bind(rollbar);
        processError(e, boundRollbarError, `Failed to fetch charts for folder ${folderId}`);
        return [];
      }
    },
    {
      placeholderData: [],
    },
  );
};

export const useGetFolderDashboards = (folderId: string | null): UseQueryResult<DashboardDetails[], unknown> => {
  const rollbar = useRollbar();
  const { fetchGet } = useRecoilValue(fetchSelector);

  return useQuery(
    ['folder-dashboards', folderId],
    async () => {
      if (!folderId) return [];

      try {
        return await fetchGet<DashboardDetails[]>(`${API_URLS.FOLDERS}/${folderId}/dashboards`);
      } catch (e) {
        const boundRollbarError = rollbar.error.bind(rollbar);
        processError(e, boundRollbarError, `Failed to fetch dashboards for folder ${folderId}`);
        return [];
      }
    },
    {
      placeholderData: [],
    },
  );
};
